import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createMaintenanceConfiguration, updateMaintenanceConfiguration } from '../../../services/maintenanceService';
import {
  ConfigurationTriggerType,
  MaintenanceConfiguration,
  MaintenanceConfigurationRequest,
} from '../../../types/maintenances';
import { preventMinus } from '../../../utils/forms';
import ConfigurationTriggerTypeSelect from '../../shared/selects/ConfigurationTriggerTypeSelect';
import MaintenanceTypeSelect from '../../shared/selects/MaintenanceTypeSelect';

type Props = {
  maintenanceConfiguration?: MaintenanceConfiguration;
  onCancel: () => void;
  afterSave: () => void;
};

export function MaintenanceConfigurationForm({ maintenanceConfiguration, onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const [editingMaintenanceConfiguration, setEditingMaintenanceConfiguration] =
    useState<MaintenanceConfigurationRequest>({
      description: '',
      maintenanceTypeId: '',
      name: '',
      trigger: 0,
      triggerType: ConfigurationTriggerType.distance,
    });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (maintenanceConfiguration) {
      setEditingMaintenanceConfiguration({
        description: maintenanceConfiguration.description,
        maintenanceTypeId: maintenanceConfiguration.maintenanceType._id,
        name: maintenanceConfiguration.name,
        trigger: maintenanceConfiguration.trigger,
        triggerType: maintenanceConfiguration.triggerType,
      });
    }
  }, [maintenanceConfiguration]);

  const canSubmit = useMemo(() => {
    return (
      !!editingMaintenanceConfiguration.name &&
      !!editingMaintenanceConfiguration.description &&
      !!editingMaintenanceConfiguration.trigger &&
      !!editingMaintenanceConfiguration.triggerType &&
      !!editingMaintenanceConfiguration.maintenanceTypeId
    );
  }, [editingMaintenanceConfiguration]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof MaintenanceConfigurationRequest) => {
    setEditingMaintenanceConfiguration((prev) => ({ ...prev!, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = maintenanceConfiguration
      ? await updateMaintenanceConfiguration(maintenanceConfiguration._id, editingMaintenanceConfiguration)
      : await createMaintenanceConfiguration(editingMaintenanceConfiguration);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        maintenanceConfiguration
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.maintenanceConfiguration, editingMaintenanceConfiguration.name)
          : FEEDBACK.created(FEEDBACK_PREFIXES.maintenanceConfiguration)
      );
    } else {
      showFailFeedback(
        error ||
          (maintenanceConfiguration
            ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.maintenanceConfiguration, editingMaintenanceConfiguration.name)
            : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.maintenanceConfiguration))
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <LabeledInput
          label="Nombre:"
          value={editingMaintenanceConfiguration?.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <LabeledInput
          label="Descripción:"
          value={editingMaintenanceConfiguration?.description}
          onChange={(value) => onInputChange(value, 'description')}
          required
        />
        <MaintenanceTypeSelect
          selectedMaintenanceTypeId={editingMaintenanceConfiguration.maintenanceTypeId}
          onChangeMaintenanceTypeId={(maintenanceTypeId) => {
            onInputChange(maintenanceTypeId, 'maintenanceTypeId');
          }}
          required
        />
        <ConfigurationTriggerTypeSelect
          selectedId={editingMaintenanceConfiguration.triggerType}
          onChangeId={(triggerTypeId) => {
            onInputChange(triggerTypeId, 'triggerType');
          }}
          disabled={!!maintenanceConfiguration}
          required
        />
        <LabeledInput
          label="Valor de activación:"
          value={editingMaintenanceConfiguration?.trigger}
          onChange={(value) => onInputChange(value, 'trigger')}
          type="number"
          min={0}
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          endEnhancer={
            editingMaintenanceConfiguration.triggerType === ConfigurationTriggerType.distance
              ? 'km'
              : editingMaintenanceConfiguration.triggerType === ConfigurationTriggerType.days
              ? 'días'
              : undefined
          }
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {maintenanceConfiguration?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
